import { useEffect, useMemo, useState } from "react";

type LensKey = "k" | "c" | "v" | "r";

const NM: Record<LensKey, string> = { k: "Gita lens", c: "Chanakya", v: "Vidur", r: "Ramayan lens" };
const lensLabel = (k: LensKey) => (NM[k].toLowerCase().endsWith("lens") ? NM[k] : `${NM[k]} lens`);
const SRC: Record<LensKey, string> = {
  k: "Bhagavad Gita",
  c: "Arthashastra",
  v: "Mahabharata",
  r: "Ramayan",
};
const ANG: Record<LensKey, number> = { k: 0, c: 90, v: 180, r: 270 };
const ATTR: Record<LensKey, string> = { k: "action", c: "strategy", v: "truth", r: "vow" };
const PIG: Record<LensKey, string> = {
  k: "hsl(var(--lens-krishna))",
  c: "hsl(var(--lens-chanakya))",
  v: "hsl(var(--lens-vidur))",
  r: "hsl(var(--lens-ram))",
};
const HUB: Record<LensKey, { dev: string; en: string }> = {
  k: { dev: "कर्म", en: "action" },
  c: { dev: "नीति", en: "strategy" },
  v: { dev: "सत्य", en: "truth" },
  r: { dev: "व्रत", en: "vow" },
};

const SECTOR: Record<LensKey, string> = {
  k: "M300,300 L146.1,129.1 A230,230 0 0 1 453.9,129.1 Z",
  c: "M300,300 L470.9,146.1 A230,230 0 0 1 470.9,453.9 Z",
  v: "M300,300 L453.9,470.9 A230,230 0 0 1 146.1,470.9 Z",
  r: "M300,300 L129.1,453.9 A230,230 0 0 1 129.1,146.1 Z",
};

type Take = { s: string; cite: string };

const DILEMMAS: {
  label: string;
  takes: Record<LensKey, Take>;
  verdict: { a: string; cite: string };
}[] = [
  {
    label: "the counter-offer",
    takes: {
      k: {
        s: "You answered this when you chose to leave. Do the work in front of you; let the offer fall away.",
        cite: "◆ Gita, Ch. 2 — acting without clinging to results",
      },
      c: {
        s: "They match your number only when losing you costs more than keeping you. Read the leverage, not the flattery.",
        cite: "◆ Arthaśāstra, Book 5 — incentive and retention",
      },
      v: {
        s: "You wanted out before the money appeared. Say the real reason plainly — first to yourself.",
        cite: "◆ Vidura Niti — the truth one avoids",
      },
      r: {
        s: "A word given is a debt owed. A raise does not dissolve a commitment already made.",
        cite: "◆ Ramayan — the weight of one's word",
      },
    },
    verdict: {
      a: "Decline it in writing, within two days.",
      cite: "◆ Arthaśāstra, Book 5 — what money cannot retain",
    },
  },
  {
    label: "stay, or leave",
    takes: {
      k: {
        s: "Fear of the unknown is not wisdom about it. Act from clarity, not from comfort.",
        cite: "◆ Gita, Ch. 3 — acting from one's nature",
      },
      c: {
        s: "Map the downside before the upside. If failure is survivable, the safe role is the real risk.",
        cite: "◆ Arthaśāstra, Book 7 — calculated risk",
      },
      v: {
        s: "Comfort rarely tells the truth about itself. Ask what you'd regret in ten years, not ten days.",
        cite: "◆ Vidura Niti — foresight",
      },
      r: {
        s: "If duty and growth point the same way, hesitation is the only wrong move.",
        cite: "◆ Ramayan — the rightful path",
      },
    },
    verdict: {
      a: "Leave — but line up three months of runway first.",
      cite: "◆ Arthaśāstra, Book 7 — preparation before the move",
    },
  },
  {
    label: "the boundary",
    takes: {
      k: {
        s: "Peace bought by silencing yourself is not peace. It is a debt that compounds.",
        cite: "◆ Gita, Ch. 2 — standing in one's truth",
      },
      c: {
        s: "Concede the small point loudly; hold the large one quietly. Not every hill is worth the war.",
        cite: "◆ Arthaśāstra, Book 1 — choosing conflicts",
      },
      v: {
        s: "Say the hard thing once, kindly and clearly. Then stop explaining.",
        cite: "◆ Vidura Niti — plain speech",
      },
      r: {
        s: "Honour the relationship and the boundary both. One without the other is not dharma.",
        cite: "◆ Ramayan — duty within family",
      },
    },
    verdict: {
      a: "Hold the boundary — state it once, plainly, and stop defending it.",
      cite: "◆ Vidura Niti — the hard truth, kindly",
    },
  },
];

const LENSES: LensKey[] = ["k", "c", "v", "r"];

const LABEL_POS: Record<LensKey, { x: number; y: number }> = {
  k: { x: 300, y: 118 },
  c: { x: 466, y: 296 },
  v: { x: 300, y: 482 },
  r: { x: 134, y: 296 },
};

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

export default function CouncilWheel({ className = "" }: { className?: string }) {
  const reduced = usePrefersReducedMotion();
  const [lens, setLens] = useState<LensKey>("k");
  const [cur, setCur] = useState(0);
  const [resolved, setResolved] = useState(false);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    if (reduced) {
      setDrawn(true);
      return;
    }
    const t = window.setTimeout(() => setDrawn(true), 60);
    return () => window.clearTimeout(t);
  }, [reduced]);

  const ticks = useMemo(
    () =>
      Array.from({ length: 72 }, (_, i) => {
        const long = i % 6 === 0;
        return { angle: i * 5, len: long ? 12 : 6, long };
      }),
    []
  );

  const dilemma = DILEMMAS[cur];
  const take = dilemma.takes[lens];
  const pointerAngle = resolved ? 45 : ANG[lens];
  const hubDev = resolved ? "निर्णय" : HUB[lens].dev;
  const hubEn = resolved ? "verdict" : HUB[lens].en;

  const selectLens = (l: LensKey) => {
    setLens(l);
    setResolved(false);
  };

  const ringCirc = 2 * Math.PI * 250;

  return (
    <div className={`w-full ${className}`}>
      {/* Dilemma chips */}
      <div className="flex flex-wrap items-center justify-center gap-2 mb-5">
        {DILEMMAS.map((d, i) => (
          <button
            key={d.label}
            type="button"
            onClick={() => {
              setCur(i);
              setResolved(false);
            }}
            aria-pressed={cur === i}
            aria-label={`Show the council on ${d.label}`}
            className="font-mono text-[10px] tracking-[0.14em] uppercase px-3 py-2 rounded-full border transition-colors"
            style={{
              borderColor:
                cur === i ? "hsl(var(--marigold) / 0.8)" : "hsl(var(--iron) / 0.45)",
              color:
                cur === i
                  ? "hsl(var(--marigold))"
                  : "hsl(var(--manuscript) / 0.55)",
              background:
                cur === i ? "hsl(var(--marigold) / 0.08)" : "transparent",
              minHeight: 34,
            }}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* Wheel */}
      <div className="relative w-full max-w-[560px] mx-auto">
        <svg
          viewBox="0 0 600 600"
          className="w-full h-auto"
          role="img"
          aria-label={`Council wheel — ${lensLabel(lens)} active`}
        >
          {/* Outer ring, drawn once on mount */}
          <circle
            cx="300"
            cy="300"
            r="250"
            fill="none"
            stroke="hsl(var(--marigold) / 0.55)"
            strokeWidth="1"
            strokeDasharray={ringCirc}
            strokeDashoffset={drawn ? 0 : ringCirc}
            transform="rotate(-90 300 300)"
            style={{
              transition: reduced ? undefined : "stroke-dashoffset 1.6s cubic-bezier(.16,.9,.2,1)",
            }}
          />
          {[190, 150, 100].map((r) => (
            <circle
              key={r}
              cx="300"
              cy="300"
              r={r}
              fill="none"
              stroke="hsl(var(--marigold) / 0.14)"
              strokeWidth="1"
            />
          ))}

          {/* Tick marks */}
          <g>
            {ticks.map((t) => (
              <line
                key={t.angle}
                x1="300"
                y1={300 - 250}
                x2="300"
                y2={300 - 250 + t.len}
                stroke={
                  t.long ? "hsl(var(--marigold) / 0.5)" : "hsl(var(--marigold) / 0.22)"
                }
                strokeWidth="1"
                transform={`rotate(${t.angle} 300 300)`}
              />
            ))}
          </g>

          {/* Sector wedges */}
          {LENSES.map((l) => (
            <path
              key={l}
              d={SECTOR[l]}
              fill={PIG[l]}
              fillOpacity={resolved || lens === l ? 0.42 : 0.1}
              style={{ transition: "fill-opacity .5s ease" }}
            />
          ))}

          {/* Hub */}
          <circle
            cx="300"
            cy="300"
            r="84"
            fill="hsl(var(--ink))"
            stroke="hsl(var(--marigold) / 0.4)"
            strokeWidth="1"
          />
          <text
            x="300"
            y="300"
            textAnchor="middle"
            className="font-dev"
            fill={resolved ? "hsl(var(--marigold))" : PIG[lens]}
            style={{ fontSize: 34 }}
          >
            {hubDev}
          </text>
          <text
            x="300"
            y="328"
            textAnchor="middle"
            className="font-mono"
            fill="hsl(var(--manuscript) / 0.5)"
            style={{ fontSize: 10, letterSpacing: "0.18em" }}
          >
            {hubEn}
          </text>

          {/* Pointer */}
          <g
            transform={`rotate(${pointerAngle} 300 300)`}
            style={{
              transition: reduced
                ? undefined
                : "transform 1s cubic-bezier(.16,.9,.2,1)",
            }}
          >
            <line
              x1="300"
              y1="300"
              x2="300"
              y2="228"
              stroke="hsl(var(--marigold))"
              strokeWidth="1.5"
            />
            <circle
              cx="300"
              cy="228"
              r="6"
              fill="none"
              stroke="hsl(var(--marigold))"
              strokeWidth="1.5"
            />
          </g>
        </svg>

        {/* Lens labels as real buttons, positioned over the SVG */}
        {LENSES.map((l) => {
          const p = LABEL_POS[l];
          const active = lens === l && !resolved;
          return (
            <button
              key={l}
              type="button"
              onClick={() => selectLens(l)}
              aria-pressed={active}
              aria-label={`${lensLabel(l)} — ${SRC[l]}, ${ATTR[l]}`}
              className="absolute -translate-x-1/2 -translate-y-1/2 px-2 py-1 text-center transition-opacity"
              style={{
                left: `${(p.x / 600) * 100}%`,
                top: `${(p.y / 600) * 100}%`,
                opacity: active ? 1 : 0.45,
              }}
            >
              <span
                className="block font-display leading-tight"
                style={{
                  fontSize: "clamp(0.85rem, 1.5vw, 1.05rem)",
                  color: active ? PIG[l] : "hsl(var(--manuscript))",
                }}
              >
                {NM[l]}
              </span>
              <span
                className="block font-mono uppercase"
                style={{
                  fontSize: 9,
                  letterSpacing: "0.2em",
                  color: "hsl(var(--manuscript) / 0.6)",
                }}
              >
                {ATTR[l]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Reasoning panel */}
      <div
        className="mt-6 p-5"
        style={{
          border: "1px solid hsl(var(--iron) / 0.4)",
          borderRadius: "var(--radius-soft)",
          background: "hsl(var(--card))",
        }}
      >
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="inline-block"
            style={{
              width: 8,
              height: 8,
              borderRadius: "var(--radius-pill)",
              background: resolved ? "hsl(var(--marigold))" : PIG[lens],
            }}
          />
          <span
            className="font-mono uppercase"
            style={{
              fontSize: 10,
              letterSpacing: "0.18em",
              color: "hsl(var(--manuscript) / 0.6)",
            }}
          >
            {resolved ? "The verdict" : `${NM[lens]} — ${SRC[lens]}`}
          </span>
        </div>

        <p
          className="font-display italic mt-3"
          style={{
            fontSize: "clamp(1rem, 1.5vw, 1.15rem)",
            lineHeight: 1.55,
            color: "hsl(var(--manuscript))",
          }}
        >
          {resolved ? dilemma.verdict.a : take.s}
        </p>

        <p
          className="font-mono mt-3"
          style={{
            fontSize: 10,
            letterSpacing: "0.08em",
            color: "hsl(var(--marigold))",
          }}
        >
          {resolved ? dilemma.verdict.cite : take.cite}
        </p>

        <button
          type="button"
          onClick={() => setResolved(true)}
          disabled={resolved}
          aria-label="Resolve this dilemma and show the verdict"
          className="font-mono uppercase mt-5 px-4 py-2 border transition-colors disabled:opacity-40"
          style={{
            fontSize: 10,
            letterSpacing: "0.2em",
            borderColor: "hsl(var(--marigold) / 0.7)",
            color: "hsl(var(--marigold))",
            borderRadius: "var(--radius-hairline)",
            minHeight: 40,
          }}
        >
          Resolve → the verdict
        </button>
      </div>
    </div>
  );
}
